import { useMemo } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { Avatar } from "../components/Avatar";
import { CapacityBar } from "../components/CapacityBar";
import type { BarSegment } from "../components/CapacityBar";
import { GroupHeader, Panel } from "../components/Panel";
import { PriorityBadge } from "../components/Priority";
import { useCollapsedGroups } from "../components/RequestList";
import { buildCapacity } from "../data/capacity";
import type { CapacityView, PersonCapacity, QueuedRequest, Risk } from "../data/capacity";
import { useRoster } from "../data/RosterContext";
import { formatIsoDate } from "../../../src/lib/dates";
import { describeDays } from "../../../src/shared/capacity";
import type { CapacityData } from "../data/loaders";

/** The window, until a selector earns its place. */
const WEEKS = 2;

/** One decimal, no float noise: 3.4000000000000004 reads as 3.4. */
const fmt = (n: number): string => String(Math.round(n * 10) / 10);

/** How full someone is, as a word first and a colour second. */
function utilisation(p: PersonCapacity): { word: string; className: string } {
  if (p.availableDays <= 0) return { word: "no capacity set", className: "text-[var(--text-2)]" };
  const ratio = p.committed.days / p.availableDays;
  if (ratio > 1) return { word: `over by ${fmt(p.committed.days - p.availableDays)} days`, className: "text-error-600" };
  if (ratio > 0.85) return { word: "near capacity", className: "text-warning-700" };
  if (ratio >= 0.5) return { word: "on track", className: "text-success-600" };
  return { word: "has room", className: "text-[var(--text-2)]" };
}

/** The whole row as one sentence — the bar's accessible name, and the single
 *  assertion a test needs to cover the computation. */
function sentence(p: PersonCapacity, weeks: number): string {
  const base = `${p.owner.name}: ${fmt(p.committed.days)} days committed against ${fmt(p.availableDays)} days of capacity in the next ${weeks} weeks — ${utilisation(p).word}.`;
  const u = p.committed.unestimated;
  return u > 0 ? `${base} ${u} ${u === 1 ? "request" : "requests"} without an estimate.` : base;
}

const RISK_LABEL: Record<Exclude<Risk["kind"], "none">, string> = { commitment: "our commitment", "needed-by": "needed-by", milestone: "milestone" };

function Projection({ item }: { item: QueuedRequest }) {
  if (item.remainingDays === null) return <span className="text-[var(--text-3)]">unsized</span>;
  if (item.projected === null) return <span className="text-[var(--text-3)]">beyond the horizon</span>;
  return (
    <>
      <span>{formatIsoDate(item.projected)}</span>
      {item.risk.kind !== "none" && (
        <small className="block text-xs text-error-600">
          {item.risk.daysLate} {item.risk.daysLate === 1 ? "day" : "days"} past {RISK_LABEL[item.risk.kind]} {formatIsoDate(item.risk.date)}
        </small>
      )}
    </>
  );
}

function WorkRow({ item }: { item: QueuedRequest }) {
  const r = item.request;
  return (
    <Link
      to={`/requests/${r.id}`}
      className="grid grid-cols-[26px_minmax(0,1fr)_64px_96px_150px] items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-100 focus-visible:focus-ring"
    >
      <PriorityBadge priority={r.priority} />
      <span className="min-w-0">
        <span className="block truncate">{r.title}</span>
        <small className="block truncate text-xs text-[var(--text-2)]">
          {r.id} · {r.account}
        </small>
      </span>
      <span className="text-right text-xs text-[var(--text-2)]">{item.estimateDays === null ? "—" : describeDays(item.estimateDays)}</span>
      <span className={`text-right text-xs ${item.overBy > 0 ? "text-error-600" : "text-[var(--text-2)]"}`}>
        {item.overBy > 0 ? `over by ${fmt(item.overBy)} d` : item.remainingDays === null ? "" : `${fmt(item.remainingDays)} d left`}
      </span>
      <span className="text-right text-xs">
        <Projection item={item} />
      </span>
    </Link>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full border border-[var(--border)] px-2 py-0.5 text-xs text-[var(--text-2)]">{children}</span>;
}

function PersonCard({ p, scale, collapsed, onToggle }: { p: PersonCapacity; scale: number; collapsed: boolean; onToggle: () => void }) {
  const id = `cap-${p.owner.id}`;
  const segments: BarSegment[] = [...p.active, ...p.queue]
    .filter((i) => i.remainingDays !== null)
    .map((i) => ({ id: i.request.id, priority: i.request.priority, days: i.remainingDays ?? 0, active: p.active.includes(i) }));
  const u = utilisation(p);
  const chips = [
    // No "· 0 d" when the parked work is unsized: zero is not what we know about it.
    p.parked.count > 0 ? `${p.parked.count} parked${p.parked.days > 0 ? ` · ${fmt(p.parked.days)} d` : ""}` : null,
    p.verifying.count > 0 ? `${p.verifying.count} in validation` : null,
    p.committed.unestimated > 0 ? `${p.committed.unestimated} unsized` : null,
  ].filter((c): c is string => c !== null);

  return (
    <Panel labelledBy={`${id}-h`}>
      <div className="grid grid-cols-[28px_minmax(0,180px)_minmax(0,1fr)_auto] items-center gap-4 px-4 py-3">
        <Avatar owner={p.owner} />
        <span className="min-w-0">
          <h2 id={`${id}-h`} className="truncate text-sm font-semibold text-gray-900">
            {p.owner.name}
          </h2>
          <small className="block text-xs text-[var(--text-2)]">{p.capacityDaysPerWeek} d/wk</small>
        </span>
        <CapacityBar segments={segments} capacityDays={p.availableDays} scale={scale} unestimated={p.committed.unestimated} label={sentence(p, WEEKS)} />
        <span className="text-right text-sm">
          <span className="font-medium">
            {fmt(p.committed.days)} / {fmt(p.availableDays)} d
          </span>
          <small className={`block text-xs ${u.className}`}>{u.word}</small>
        </span>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pb-3">
          {chips.map((c) => (
            <Chip key={c}>{c}</Chip>
          ))}
        </div>
      )}
      <GroupHeader label="Now" count={p.active.length} />
      {p.active.length > 0 ? p.active.map((i) => <WorkRow key={i.request.id} item={i} />) : <p className="px-4 py-2 text-xs text-[var(--text-3)]">Nothing in progress.</p>}
      <GroupHeader label="Next" count={p.queue.length} collapse={{ collapsed, onToggle, controls: `${id}-next` }} />
      <div id={`${id}-next`} hidden={collapsed}>
        {p.queue.length > 0 ? p.queue.map((i) => <WorkRow key={i.request.id} item={i} />) : <p className="px-4 py-2 text-xs text-[var(--text-3)]">Nothing queued.</p>}
      </div>
    </Panel>
  );
}

function TeamStrip({ view }: { view: CapacityView }) {
  const committed = view.people.reduce((n, p) => n + p.committed.days, 0);
  const available = view.people.reduce((n, p) => n + p.availableDays, 0);
  const unestimated = view.people.reduce((n, p) => n + p.committed.unestimated, 0) + view.unassigned.unestimated;
  const atRisk = view.people.reduce((n, p) => n + [...p.active, ...p.queue].filter((i) => i.risk.kind !== "none").length, 0);
  const stat = (label: string, value: string, tone = "") => (
    <span className="text-sm">
      <span className={`block font-semibold ${tone}`}>{value}</span>
      <span className="text-xs text-[var(--text-2)]">{label}</span>
    </span>
  );
  return (
    <div className="flex flex-wrap gap-6">
      {stat("days committed / available", `${fmt(committed)} / ${fmt(available)}`, committed > available ? "text-error-600" : "")}
      {stat("without an estimate", String(unestimated), unestimated > 0 ? "text-warning-700" : "")}
      {stat("past a promise", String(atRisk), atRisk > 0 ? "text-error-600" : "")}
      {stat("unassigned", `${view.unassigned.count} · ${fmt(view.unassigned.days)} d`)}
    </div>
  );
}

/**
 * Who is working on what, how loaded they are against their own days, and
 * what is next for them in order — derived from the request list and the
 * roster on every render, with nothing stored. See app/src/data/capacity.ts
 * for the rule; this file is layout.
 */
export function Capacity() {
  const { requests } = useLoaderData() as CapacityData;
  const { members } = useRoster();
  const view = useMemo(() => buildCapacity(requests, members, { weeks: WEEKS }), [requests, members]);
  // One scale for every bar, so lengths compare across people (LoadBar's rule).
  const scale = Math.max(1, ...view.people.map((p) => Math.max(p.availableDays, p.committed.days)));
  const [collapsed, toggle] = useCollapsedGroups("ce-hub:collapsed-capacity:staff");

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-display-xs font-semibold text-gray-900">Capacity</h1>
          <p className="mt-1 text-sm text-[var(--text-2)]">The next {WEEKS} weeks from {formatIsoDate(view.today)}. Waiting, Validation and Escalated are not counted — they are not ours to schedule.</p>
        </div>
        <TeamStrip view={view} />
      </header>

      <div className="grid gap-4">
        {view.people.map((p) => (
          <PersonCard key={p.owner.id} p={p} scale={scale} collapsed={collapsed.has(p.owner.id)} onToggle={() => toggle(p.owner.id)} />
        ))}
      </div>

      <p className="mt-6 text-xs text-[var(--text-2)]">
        In-progress work is charged what remains of its estimate: each weekday since it last entered In progress is shared between the items that were active that day, and work done before a pause is not credited. When more has elapsed than was
        estimated, the row says by how much. Weekends are skipped; holidays and leave are not modelled, so dates run optimistic across them. Nothing here is a commitment — the commitment date on each request is.
      </p>
    </div>
  );
}
